import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Box,
  Typography,
  TextField,
  Chip,
  Avatar,
  Tabs,
  Tab,
  List,
  ListItem,
  ListItemText,
  ListItemAvatar,
  Divider,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  IconButton,
  CircularProgress,
  Alert,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
} from '@mui/material';
import {
  Close as CloseIcon,
  Edit as EditIcon,
  Save as SaveIcon,
  Send as SendIcon,
  AccessTime as TimeIcon,
  Person as PersonIcon,
} from '@mui/icons-material';
import DeveloperDashboardService from '../../services/DeveloperDashboardService';

interface TaskDetailModalProps {
  open: boolean;
  taskId: number | null;
  onClose: () => void;
  onTaskUpdated?: () => void;
}

interface TabPanelProps {
  children?: React.ReactNode;
  index: number;
  value: number;
}

function TabPanel(props: TabPanelProps) {
  const { children, value, index, ...other } = props;
  return (
    <div hidden={value !== index} {...other}>
      {value === index && <Box sx={{ py: 3 }}>{children}</Box>}
    </div>
  );
}

const TaskDetailModal: React.FC<TaskDetailModalProps> = ({
  open,
  taskId,
  onClose,
  onTaskUpdated,
}) => {
  const [currentTab, setCurrentTab] = useState(0);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [task, setTask] = useState<any>(null);
  const [comments, setComments] = useState<any[]>([]);
  const [timeLogs, setTimeLogs] = useState<any[]>([]);
  const [editing, setEditing] = useState(false);
  
  // Form states
  const [title, setTitle] = useState('');
  const [description, setDescription] = useState('');
  const [priority, setPriority] = useState('normal');
  const [dueDate, setDueDate] = useState('');
  const [estimatedHours, setEstimatedHours] = useState<number | ''>('');
  
  // Comment form
  const [newComment, setNewComment] = useState('');
  const [submittingComment, setSubmittingComment] = useState(false);
  
  // Time log form
  const [timeHours, setTimeHours] = useState<number | ''>('');
  const [timeDate, setTimeDate] = useState(new Date().toISOString().split('T')[0]);
  const [timeDescription, setTimeDescription] = useState('');
  const [submittingTime, setSubmittingTime] = useState(false);

  useEffect(() => {
    console.log('TaskDetailModal useEffect:', { open, taskId });
    if (open && taskId) {
      loadTaskData();
    }
  }, [open, taskId]);

  const loadTaskData = async () => {
    console.log('loadTaskData called with taskId:', taskId);
    if (!taskId) return;
    
    try {
      setLoading(true);
      setError(null);
      
      const [taskData, commentsData, timeLogsData] = await Promise.all([
        DeveloperDashboardService.getTask(taskId),
        DeveloperDashboardService.getTaskComments(taskId),
        DeveloperDashboardService.getTimeLogs(taskId),
      ]);
      
      setTask(taskData);
      setComments(commentsData);
      setTimeLogs(timeLogsData);
      
      // Set form values
      setTitle(taskData.title);
      setDescription(taskData.description || '');
      setPriority(taskData.priority);
      setDueDate(taskData.dueDate || '');
      setEstimatedHours(taskData.estimatedHours || '');
      
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load task');
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async () => {
    if (!taskId) return;
    
    try {
      setLoading(true);
      setError(null);
      
      await DeveloperDashboardService.updateTask(taskId, {
        title,
        description,
        priority: priority as 'low' | 'normal' | 'high' | 'urgent',
        dueDate: dueDate || undefined,
        estimatedHours: estimatedHours || undefined,
      });
      
      setEditing(false);
      await loadTaskData();
      onTaskUpdated?.();
      
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to save task');
    } finally {
      setLoading(false);
    }
  };

  const handleAddComment = async () => {
    if (!taskId || !newComment.trim()) return;
    
    try {
      setSubmittingComment(true);
      setError(null);
      
      await DeveloperDashboardService.addTaskComment(taskId, newComment);
      setNewComment('');
      
      // Reload comments
      const commentsData = await DeveloperDashboardService.getTaskComments(taskId);
      setComments(commentsData);
      
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to add comment');
    } finally {
      setSubmittingComment(false);
    }
  };

  const handleLogTime = async () => {
    if (!taskId || !timeHours || timeHours <= 0) return;
    
    try {
      setSubmittingTime(true);
      setError(null);
      
      await DeveloperDashboardService.logTime(taskId, {
        hours: Number(timeHours),
        date: timeDate,
        description: timeDescription,
      });
      
      // Reset form
      setTimeHours('');
      setTimeDate(new Date().toISOString().split('T')[0]);
      setTimeDescription('');
      
      // Reload data
      await loadTaskData();
      onTaskUpdated?.();
      
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to log time');
    } finally {
      setSubmittingTime(false);
    }
  };

  const handleClose = () => {
    setEditing(false);
    setCurrentTab(0);
    setNewComment('');
    setTimeHours('');
    setTimeDescription('');
    onClose();
  };

  const getPriorityColor = (priority: string) => {
    switch (priority) {
      case 'urgent': return 'error';
      case 'high': return 'warning';
      case 'normal': return 'info';
      case 'low': return 'default';
      default: return 'default';
    }
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'done': return 'success';
      case 'in_progress': return 'primary';
      case 'in_review': return 'secondary';
      case 'testing': return 'warning';
      default: return 'default';
    }
  };

  if (!open) return null;

  return (
    <Dialog 
      open={open} 
      onClose={handleClose}
      maxWidth="md"
      fullWidth
      PaperProps={{
        sx: { minHeight: '80vh' }
      }}
    >
      <DialogTitle>
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            {editing ? (
              <TextField
                value={title}
                onChange={(e) => setTitle(e.target.value)}
                variant="standard"
                fullWidth
                sx={{ mr: 2 }}
              />
            ) : (
              <Typography variant="h6">{task?.title || 'Loading...'}</Typography>
            )}
          </Box>
          <Box sx={{ display: 'flex', gap: 1 }}>
            {editing ? (
              <>
                <IconButton onClick={handleSave} disabled={loading}>
                  <SaveIcon />
                </IconButton>
                <IconButton onClick={() => setEditing(false)}>
                  <CloseIcon />
                </IconButton>
              </>
            ) : (
              <IconButton onClick={() => setEditing(true)}>
                <EditIcon />
              </IconButton>
            )}
            <IconButton onClick={handleClose}>
              <CloseIcon />
            </IconButton>
          </Box>
        </Box>
      </DialogTitle>

      <DialogContent dividers>
        {loading && !task ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
            <CircularProgress />
          </Box>
        ) : error ? (
          <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>
        ) : task ? (
          <>
            {/* Task Header */}
            <Box sx={{ mb: 3 }}>
              <Box sx={{ display: 'flex', gap: 1, mb: 2, flexWrap: 'wrap' }}>
                <Chip 
                  label={task.status.replace('_', ' ').toUpperCase()} 
                  color={getStatusColor(task.status)}
                  size="small"
                />
                <Chip 
                  label={priority.toUpperCase()} 
                  color={getPriorityColor(priority)}
                  size="small"
                />
                <Chip 
                  label={task.type.toUpperCase()} 
                  variant="outlined"
                  size="small"
                />
              </Box>

              <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2 }}>
                <Avatar sx={{ bgcolor: 'primary.main' }}>
                  {task.assignee?.name?.charAt(0) || 'U'}
                </Avatar>
                <Box>
                  <Typography variant="body2" color="text.secondary">
                    Assigned to
                  </Typography>
                  <Typography variant="body1">
                    {task.assignee?.name || 'Unassigned'}
                  </Typography>
                </Box>
              </Box>

              <Box sx={{ display: 'flex', gap: 4, flexWrap: 'wrap' }}>
                <Box>
                  <Typography variant="body2" color="text.secondary">Project</Typography>
                  <Typography variant="body1">{task.project}</Typography>
                </Box>
                {(editing ? dueDate : task.dueDate) && (
                  <Box>
                    <Typography variant="body2" color="text.secondary">Due Date</Typography>
                    <Typography variant="body1">
                      {editing ? dueDate : task.dueDate}
                    </Typography>
                  </Box>
                )}
                <Box>
                  <Typography variant="body2" color="text.secondary">Estimated Hours</Typography>
                  <Typography variant="body1">
                    {editing ? estimatedHours : task.estimatedHours || 'Not set'}
                  </Typography>
                </Box>
                <Box>
                  <Typography variant="body2" color="text.secondary">Logged Hours</Typography>
                  <Typography variant="body1">{task.loggedHours || 0}</Typography>
                </Box>
              </Box>
            </Box>

            {/* Tabs */}
            <Tabs value={currentTab} onChange={(_, val) => setCurrentTab(val)}>
              <Tab label="Details" />
              <Tab label={`Comments (${comments.length})`} />
              <Tab label={`Time Logs (${timeLogs.length})`} />
            </Tabs>

            {/* Tab Panels */}
            <TabPanel value={currentTab} index={0}>
              {editing ? (
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                  <TextField
                    label="Description"
                    value={description}
                    onChange={(e) => setDescription(e.target.value)}
                    multiline
                    rows={6}
                    fullWidth
                  />
                  <FormControl fullWidth>
                    <InputLabel>Priority</InputLabel>
                    <Select
                      value={priority}
                      onChange={(e) => setPriority(e.target.value)}
                      label="Priority"
                    >
                      <MenuItem value="low">Low</MenuItem>
                      <MenuItem value="normal">Normal</MenuItem>
                      <MenuItem value="high">High</MenuItem>
                      <MenuItem value="urgent">Urgent</MenuItem>
                    </Select>
                  </FormControl>
                  <TextField
                    label="Due Date"
                    type="date"
                    value={dueDate}
                    onChange={(e) => setDueDate(e.target.value)}
                    InputLabelProps={{ shrink: true }}
                    fullWidth
                  />
                  <TextField
                    label="Estimated Hours"
                    type="number"
                    value={estimatedHours}
                    onChange={(e) => setEstimatedHours(e.target.value ? Number(e.target.value) : '')}
                    fullWidth
                  />
                </Box>
              ) : (
                <Box>
                  <Typography variant="h6" gutterBottom>Description</Typography>
                  <Typography variant="body1" sx={{ whiteSpace: 'pre-wrap' }}>
                    {task.description || 'No description provided'}
                  </Typography>

                  {task.acceptanceCriteria && (
                    <Box sx={{ mt: 3 }}>
                      <Typography variant="h6" gutterBottom>Acceptance Criteria</Typography>
                      <Typography variant="body1" sx={{ whiteSpace: 'pre-wrap' }}>
                        {task.acceptanceCriteria}
                      </Typography>
                    </Box>
                  )}

                  {task.notes && (
                    <Box sx={{ mt: 3 }}>
                      <Typography variant="h6" gutterBottom>Notes</Typography>
                      <Typography variant="body1" sx={{ whiteSpace: 'pre-wrap' }}>
                        {task.notes}
                      </Typography>
                    </Box>
                  )}
                </Box>
              )}
            </TabPanel>

            <TabPanel value={currentTab} index={1}>
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                <Box sx={{ display: 'flex', gap: 1 }}>
                  <TextField
                    placeholder="Add a comment..."
                    value={newComment}
                    onChange={(e) => setNewComment(e.target.value)}
                    multiline
                    rows={2}
                    fullWidth
                  />
                  <Button
                    variant="contained"
                    onClick={handleAddComment}
                    disabled={!newComment.trim() || submittingComment}
                    startIcon={<SendIcon />}
                  >
                    Send
                  </Button>
                </Box>

                <List>
                  {comments.map((comment, index) => (
                    <React.Fragment key={comment.id}>
                      <ListItem alignItems="flex-start">
                        <ListItemAvatar>
                          <Avatar>
                            <PersonIcon />
                          </Avatar>
                        </ListItemAvatar>
                        <ListItemText
                          primary={
                            <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                              <Typography variant="subtitle2">{comment.userName}</Typography>
                              <Typography variant="caption" color="text.secondary">
                                {new Date(comment.createdAt).toLocaleString()}
                              </Typography>
                            </Box>
                          }
                          secondary={comment.content}
                        />
                      </ListItem>
                      {index < comments.length - 1 && <Divider variant="inset" component="li" />}
                    </React.Fragment>
                  ))}
                  {comments.length === 0 && (
                    <Typography variant="body2" color="text.secondary" sx={{ textAlign: 'center', py: 2 }}>
                      No comments yet
                    </Typography>
                  )}
                </List>
              </Box>
            </TabPanel>

            <TabPanel value={currentTab} index={2}>
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                {/* Time Log Form */}
                <Paper sx={{ p: 2, bgcolor: 'grey.50' }}>
                  <Typography variant="h6" gutterBottom>Log Time</Typography>
                  <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
                    <TextField
                      label="Hours"
                      type="number"
                      value={timeHours}
                      onChange={(e) => setTimeHours(e.target.value ? Number(e.target.value) : '')}
                      inputProps={{ min: 0.25, max: 24, step: 0.25 }}
                      sx={{ width: 120 }}
                    />
                    <TextField
                      label="Date"
                      type="date"
                      value={timeDate}
                      onChange={(e) => setTimeDate(e.target.value)}
                      InputLabelProps={{ shrink: true }}
                      sx={{ width: 160 }}
                    />
                    <TextField
                      label="Description (optional)"
                      value={timeDescription}
                      onChange={(e) => setTimeDescription(e.target.value)}
                      sx={{ flexGrow: 1, minWidth: 200 }}
                    />
                    <Button
                      variant="contained"
                      onClick={handleLogTime}
                      disabled={!timeHours || submittingTime}
                      startIcon={<TimeIcon />}
                    >
                      Log
                    </Button>
                  </Box>
                </Paper>

                {/* Time Logs Table */}
                <TableContainer component={Paper}>
                  <Table size="small">
                    <TableHead>
                      <TableRow>
                        <TableCell>Date</TableCell>
                        <TableCell>User</TableCell>
                        <TableCell align="right">Hours</TableCell>
                        <TableCell>Description</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {timeLogs.map((log) => (
                        <TableRow key={log.id}>
                          <TableCell>{log.date}</TableCell>
                          <TableCell>{log.userName}</TableCell>
                          <TableCell align="right">{log.hours}</TableCell>
                          <TableCell>{log.description || '-'}</TableCell>
                        </TableRow>
                      ))}
                      {timeLogs.length === 0 && (
                        <TableRow>
                          <TableCell colSpan={4} align="center">
                            <Typography variant="body2" color="text.secondary">
                              No time logs yet
                            </Typography>
                          </TableCell>
                        </TableRow>
                      )}
                    </TableBody>
                  </Table>
                </TableContainer>

                {timeLogs.length > 0 && (
                  <Box sx={{ textAlign: 'right' }}>
                    <Typography variant="h6">
                      Total: {timeLogs.reduce((sum, log) => sum + log.hours, 0).toFixed(2)} hours
                    </Typography>
                  </Box>
                )}
              </Box>
            </TabPanel>
          </>
        ) : null}
      </DialogContent>

      <DialogActions>
        <Button onClick={handleClose}>Close</Button>
      </DialogActions>
    </Dialog>
  );
};

export default TaskDetailModal;
